/**
 * 消息气泡内的附件区 + 页面层大图预览（T23）。
 *
 * 为什么不直接把 T09 的 `AttachmentList` 塞进气泡：`AttachmentList` 的容器与
 * 大图遮罩都用 `contentBoxStyle()` 按「页面根容器」绝对定位，一旦放进可滚动的
 * 消息气泡，缩略图会相对气泡错位、遮罩会落到气泡内部而不是整屏。因此这里：
 * - 复用 T09 的 `Attachment` 契约、`formatFileSize()` 与「坐标换算函数」
 *   （`messageAttachmentImageLayout` / `messageAttachmentFileLayout` 内部就是
 *   T09 的 `listImageLayout` / `listFileLayout`），保证清单坐标与 T09 一致；
 * - 缩略图 / 文件卡片在气泡里按流式布局渲染（占位与正文一致，不依赖绝对定位）；
 * - 大图预览由页面层组件 `MessageImagePreview` 承担，坐标与动效与 T09 同源。
 *
 * 每个可见部件都通过 `useComponentMeta()` 登记 390x844 逻辑坐标。
 */

import { motion } from 'framer-motion';
import type { CSSProperties, ReactElement } from 'react';

import type { Attachment } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import { formatFileSize } from '../attachments/validate';
import styles from './ChatPage.module.css';
import {
  PAGE_TOP,
  chatComponentMeta,
  chatLayout,
  messageAttachmentFileLayout,
  messageAttachmentImageLayout,
  messageAttachmentsLayout,
} from './componentMeta';

/** 画布坐标 → 页面根容器内的绝对定位样式（与 ChatPage 的 boxStyle 同一约定）。 */
function pageBoxStyle(layout: BoxLayout, zIndex: number): CSSProperties {
  return {
    position: 'absolute',
    left: layout.x,
    top: layout.y - PAGE_TOP,
    width: layout.width,
    height: layout.height,
    zIndex,
  };
}

/** 画布坐标 → 相对某个同为画布坐标的父级换算（父级必须是已定位元素）。 */
function offsetBoxStyle(layout: BoxLayout, parent: BoxLayout): CSSProperties {
  return {
    position: 'absolute',
    left: layout.x - parent.x,
    top: layout.y - parent.y,
    width: layout.width,
    height: layout.height,
  };
}

interface BubbleImageTileProps {
  attachment: Attachment;
  layout: BoxLayout;
  baseId: string;
  onPreview: (attachment: Attachment) => void;
}

function BubbleImageTile({
  attachment,
  layout,
  baseId,
  onPreview,
}: BubbleImageTileProps): ReactElement {
  const presets = useMotionPresets();
  const meta: ComponentMetaInput = {
    id: `${baseId}.attachments.image.${attachment.id}`,
    page: 'chat',
    role: 'button',
    label: `查看大图：${attachment.name}`,
    layout,
    zIndex: 6,
    notes: '消息内图片缩略图 72x72dp，点击打开页面层大图预览',
  };
  const ref = useComponentMeta<HTMLButtonElement>(buildComponentMeta(meta, 'button'));

  return (
    <motion.button
      ref={ref}
      type="button"
      className={styles.bubbleImageTile}
      data-ui-id={meta.id}
      data-ui-role="button"
      aria-label={meta.label}
      style={{ width: layout.width, height: layout.height }}
      whileTap={presets.tap.whileTap}
      transition={presets.tap.transition}
      onClick={() => {
        onPreview(attachment);
      }}
    >
      {attachment.dataUrl !== undefined ? (
        <img className={styles.bubbleImageThumb} src={attachment.dataUrl} alt={attachment.name} />
      ) : (
        <span className={styles.bubbleFileName}>{attachment.name}</span>
      )}
    </motion.button>
  );
}

interface BubbleFileCardProps {
  attachment: Attachment;
  layout: BoxLayout;
  baseId: string;
}

function BubbleFileCard({ attachment, layout, baseId }: BubbleFileCardProps): ReactElement {
  const downloadable = attachment.dataUrl !== undefined;
  const meta: ComponentMetaInput = {
    id: `${baseId}.attachments.file.${attachment.id}`,
    page: 'chat',
    role: downloadable ? 'button' : 'container',
    label: `文件：${attachment.name}`,
    layout,
    zIndex: 6,
    props: ['download'],
    notes: '显示文件名 + 大小，可下载 / 另存',
  };
  const ref = useComponentMeta<HTMLAnchorElement>(buildComponentMeta(meta, 'container'));

  return (
    <a
      ref={ref}
      className={styles.bubbleFileCard}
      data-ui-id={meta.id}
      data-ui-role={downloadable ? 'button' : 'container'}
      aria-label={meta.label}
      {...(downloadable ? { href: attachment.dataUrl, download: attachment.name } : {})}
    >
      <span className={styles.bubbleFileIcon} aria-hidden="true">
        📄
      </span>
      <span className={styles.bubbleFileMeta}>
        <span className={styles.bubbleFileName}>{attachment.name}</span>
        <span className={styles.bubbleFileSize}>{formatFileSize(attachment.size)}</span>
      </span>
    </a>
  );
}

export interface MessageAttachmentsProps {
  /** 气泡的运行时 id 前缀（`chat.message.<role>.<消息id>`）。 */
  baseId: string;
  attachments: readonly Attachment[];
  /** 该条消息的声明气泡布局（390x844 画布坐标），用于登记子部件坐标。 */
  bubble: BoxLayout;
  onPreview: (attachment: Attachment) => void;
}

/** 消息气泡内的附件区。 */
export function MessageAttachments({
  baseId,
  attachments,
  bubble,
  onPreview,
}: MessageAttachmentsProps): ReactElement | null {
  const images = attachments.filter((item) => item.kind === 'image');
  const files = attachments.filter((item) => item.kind !== 'image');
  const area = messageAttachmentsLayout(bubble, images.length, files.length);

  const meta: ComponentMetaInput = {
    id: `${baseId}.attachments`,
    page: 'chat',
    role: 'list',
    label: '消息内附件区',
    layout: area,
    zIndex: 5,
    props: ['items'],
    notes: '图片缩略图 + 文件卡片；安卓用 LinearLayout / RecyclerView',
  };
  const ref = useComponentMeta<HTMLDivElement>(buildComponentMeta(meta, 'list'));

  return (
    <div
      ref={ref}
      className={styles.bubbleAttachments}
      data-ui-id={meta.id}
      data-ui-role="list"
      role="list"
      aria-label="消息附件"
    >
      {images.length > 0 ? (
        <div className={styles.bubbleImageRow}>
          {images.map((attachment, index) => (
            <BubbleImageTile
              key={attachment.id}
              attachment={attachment}
              layout={messageAttachmentImageLayout(area, index)}
              baseId={baseId}
              onPreview={onPreview}
            />
          ))}
        </div>
      ) : null}
      {files.map((attachment, index) => (
        <BubbleFileCard
          key={attachment.id}
          attachment={attachment}
          layout={messageAttachmentFileLayout(area, index, images.length)}
          baseId={baseId}
        />
      ))}
    </div>
  );
}

export interface MessageImagePreviewProps {
  attachment: Attachment;
  onClose: () => void;
}

/** 页面层的大图预览（整屏遮罩 + 44dp 关闭按钮，动效取 motion.ts 的 sheetBackdrop）。 */
export function MessageImagePreview({
  attachment,
  onClose,
}: MessageImagePreviewProps): ReactElement {
  const presets = useMotionPresets();
  const backdropLayout = chatLayout('chat.imagePreview.backdrop');
  const imageLayout = chatLayout('chat.imagePreview.image');
  const closeLayout = chatLayout('chat.imagePreview.closeButton');

  const backdropRef = useComponentMeta<HTMLDivElement>(
    chatComponentMeta('chat.imagePreview.backdrop'),
  );
  const imageRef = useComponentMeta<HTMLImageElement>(chatComponentMeta('chat.imagePreview.image'));
  const closeRef = useComponentMeta<HTMLButtonElement>(
    chatComponentMeta('chat.imagePreview.closeButton'),
  );

  return (
    <motion.div
      ref={backdropRef}
      className={styles.previewBackdrop}
      data-ui-id="chat.imagePreview.backdrop"
      data-ui-role="modal"
      role="dialog"
      aria-modal="true"
      aria-label="图片预览"
      style={pageBoxStyle(backdropLayout, 40)}
      variants={presets.sheetBackdrop}
      initial="hidden"
      animate="visible"
      exit="hidden"
      onClick={onClose}
    >
      {attachment.dataUrl !== undefined ? (
        <img
          ref={imageRef}
          className={styles.previewImage}
          data-ui-id="chat.imagePreview.image"
          data-ui-role="icon"
          alt={attachment.name}
          src={attachment.dataUrl}
          style={offsetBoxStyle(imageLayout, backdropLayout)}
          onClick={(event) => {
            event.stopPropagation();
          }}
        />
      ) : null}
      <button
        ref={closeRef}
        type="button"
        className={styles.previewClose}
        data-ui-id="chat.imagePreview.closeButton"
        data-ui-role="button"
        aria-label="关闭消息图片预览"
        style={offsetBoxStyle(closeLayout, backdropLayout)}
        onClick={onClose}
      >
        ✕
      </button>
    </motion.div>
  );
}
